import { Box, DropdownLayoutValueOption } from '@wix/design-system'
import { useContent, useMetadata, useNext } from '@wix/funnel-builder-lizard'
import { debounce } from 'debounce'
import React, { ChangeEventHandler, FC, useCallback, useEffect, useReducer, useRef } from 'react'
import { useMediaQuery } from 'react-responsive'
import { useSearchParams } from 'react-router-dom'

import { usePageLogger } from '../../../bi/mfi'
import { onboardingPageView } from '../../../bi/onboarding'
import { LARGE_DESKTOP } from '../../../breakpoints'
import { useStorageContext } from '../../../context/StorageContext/StorageContext'
import { Button } from '../Button'
import { FancyAutoComplete } from '../FancyAutoComplete'
import { FooterControls, FooterControlsSpacerHeight, FooterControlsType } from '../FooterControls'
import { Heading } from '../Heading/Heading'
import { LogoWithBack } from '../LogoWithBack'
import { VideoBox } from '../VideoBox/VideoBox'
import { VideoLayout } from '../VideoLayout/VideoLayout'
import { WixIntrosTemplate } from '../WixIntrosTemplate'
import { BusinessTermService } from './api/BusinessTermService'
import { AutoCompleteWithOverrides } from './AutoCompleteWithOverrides'
import { dataHooks } from './Category.dataHooks'
import s from './Category.scss'
import { getInitialState, goNext, goSkip, loadCategories, reducer, selectCategory } from './state'
import { Suggestions } from './Suggestions'
import type { Content } from './types'

const debouncedLoadCategories = debounce(loadCategories, 400)

export enum CategoryLayout {
  NEW_CONCEPT = 'NEW_CONCEPT',
  WIX_INTROS = 'WIX_INTROS',
}

export type CategoryProps = {
  businessTermService: BusinessTermService
  layout?: CategoryLayout
}

export const Category: FC<CategoryProps> = props => {
  const { save, category: savedCategory } = useStorageContext()
  const { businessTermService, layout = CategoryLayout.NEW_CONCEPT } = props
  const autoCompleteRef = useRef<AutoCompleteWithOverrides>(null)
  const [{ language }] = useMetadata()

  const { content } = useContent<Content>()
  const { question, isAddUserInputAsOption } = content
  const { questionKey } = question

  const [searchParams] = useSearchParams()
  const prefilledInput = searchParams.get(questionKey) || searchParams.get('category') || ''

  const [state, dispatch] = useReducer(reducer, { savedCategory, prefilledInput }, getInitialState)
  const { search, searchSource, category, categoriesLoading, categories, clickedButton } = state

  const next = useNext()

  const isLargeDesktopScreen = useMediaQuery(LARGE_DESKTOP)

  const pageLogger = usePageLogger()

  const handleSuggestionClick = useCallback(
    (suggestion: string, index: number) => {
      void pageLogger.clickAnswer({ questionKey, buttonName: suggestion, suggestedOption: true, position: index + 1 })
      dispatch({ type: 'selectSuggestion', suggestion })
      autoCompleteRef.current?.showOptions()
    },
    [pageLogger, questionKey],
  )

  const handleSearchChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    e => dispatch({ type: 'changeSearch', search: e.target.value }),
    [],
  )
  const handleClear = useCallback(() => {
    void pageLogger.generalAction({ action: 'delete_category' })
    dispatch({ type: 'changeSearch', search: '' })
  }, [pageLogger])

  const handleSearchSelect = useCallback(
    (option: DropdownLayoutValueOption) => {
      void selectCategory({
        dispatch,
        displayName: option.id as string,
        search,
        categories,
        pageLogger,
        questionKey,
        businessTermService,
      })
    },
    [categories, pageLogger, questionKey, search, businessTermService],
  )

  const handleInputClick = useCallback(() => autoCompleteRef.current?.showOptions(), [])

  const handleNextClick = useCallback(() => {
    if (clickedButton !== undefined) {
      return
    }
    dispatch({ type: 'buttonClicked', clickedButton: 'primary' })
    goNext({ category, pageLogger, language, questionKey, next, save })
  }, [dispatch, clickedButton, category, pageLogger, language, questionKey, next, save])

  const handleSkipClick = useCallback(() => {
    if (clickedButton !== undefined) {
      return
    }
    dispatch({ type: 'buttonClicked', clickedButton: 'skip' })
    goSkip({ pageLogger, next, questionKey, language, save })
  }, [dispatch, clickedButton, pageLogger, next, questionKey, language, save])

  const handleBackClick = useCallback(() => {
    if (clickedButton !== undefined) {
      return
    }
    dispatch({ type: 'buttonClicked', clickedButton: 'back' })
    void pageLogger.generalAction({ action: 'back' })
    window.history.back()
  }, [clickedButton, dispatch, pageLogger])

  useEffect(() => {
    void pageLogger.screenView({ initialData: prefilledInput })
  }, [pageLogger])

  useEffect(() => {
    void debouncedLoadCategories({
      search,
      searchSource,
      dispatch,
      businessTermService,
      pageLogger,
      language,
      isAddUserInputAsOption,
    })
  }, [search, searchSource, dispatch, pageLogger, language, businessTermService])

  useEffect(() => {
    void onboardingPageView({ contentLanguage: language })
  }, [language])

  useEffect(() => {
    autoCompleteRef.current?.focus()
    autoCompleteRef.current?.showOptions()
  }, [])

  const categoriesOptions: DropdownLayoutValueOption[] = categories.map(category => ({
    id: category.displayNameOverride || category.displayName || '',
    value: category.displayNameOverride || category.displayName || '',
  }))

  let selectedId: string | number | undefined =
    category?.businessTerm?.displayNameOverride || category?.businessTerm?.displayName
  // this is required in order to keep the first option selected when user types something in search input
  if (isAddUserInputAsOption && !category) {
    selectedId = categoriesOptions[0]?.id || ''
  }

  const autocomplete = (
    <>
      <Box gap="SP3" verticalAlign="bottom">
        <Box direction="vertical" flexGrow="1">
          <FancyAutoComplete
            ref={autoCompleteRef}
            value={search || ''}
            selectedId={selectedId}
            onChange={handleSearchChange}
            status={categoriesLoading ? 'loading' : undefined}
            options={categoriesOptions}
            placeholder={content.placeholder}
            size="large"
            onSelect={handleSearchSelect}
            onInputClicked={handleInputClick}
            maxHeightPixels="300"
            className={s.autoComplete}
            border="bottomLine"
            onClear={categoriesLoading ? undefined : handleClear}
            customRenderer={AutoCompleteWithOverrides}
            isIconHidden={isAddUserInputAsOption}
            dataHook={dataHooks.autoComplete()}
          />
        </Box>
        <Button
          skin={layout === CategoryLayout.WIX_INTROS ? 'standard' : undefined}
          disabled={!category}
          size={isLargeDesktopScreen ? 'large' : 'medium'}
          onClick={handleNextClick}
          label={content.nextButton}
          isLoading={clickedButton === 'primary'}
          dataHook={dataHooks.nextButton()}
        />
      </Box>
      <Suggestions
        visible={!category}
        label={content.suggestionsLabel}
        items={content.suggestions.items}
        onItemClick={handleSuggestionClick}
      />
    </>
  )

  const mainContent = (
    <Box direction="vertical" className={s.autoCompleteContainer}>
      <Box marginBottom="SP5">
        <Heading size="extraLarge" className={s.title} dataHook={dataHooks.title()}>
          {content.title}
        </Heading>
      </Box>
      {autocomplete}
    </Box>
  )

  const video =
    (content.video && content.hint && (
      <VideoBox backgroundColor="D70" src={content.video} description={content.hint} />
    )) ||
    null

  const footerControls = (
    <FooterControls
      backText={content.backButton}
      backOnClick={handleBackClick}
      skipText={content.skipButton}
      skipOnClick={handleSkipClick}
      type={FooterControlsType.TRANSPARENT}
      spacerHeight={FooterControlsSpacerHeight.NONE}
      isVisible
    />
  )

  // `layout`-prop solution is good enough.
  // Until we get more requirements on BI events for Wix Intros Category page
  // reusing existing events/events names is a good solution.
  if (layout === CategoryLayout.WIX_INTROS) {
    return (
      <WixIntrosTemplate withLogo>
        <Heading size="xxl" className={s.title} dataHook={dataHooks.title()}>
          {content.title}
        </Heading>
        <Box direction="vertical" width="100%" maxWidth="500px" marginTop="SP6">
          {autocomplete}
        </Box>
        {footerControls}
      </WixIntrosTemplate>
    )
  }

  if (layout === CategoryLayout.NEW_CONCEPT) {
    return (
      <Box height="100vh" overflow="hidden">
        <LogoWithBack />
        <VideoLayout mainContent={mainContent} video={video} />
        {footerControls}
      </Box>
    )
  }

  return null
}
